<script lang="ts" generics="T">
	import type { ExtendedRow } from '#lib/hockeydata/types/util/ExtendedRow.js';
	import type { TableCol } from '#lib/hockeydata/types/util/TableCol.js';
	import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
	import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
	import _ from 'lodash';
	import type { Snippet } from 'svelte';
	import { twMerge } from 'tailwind-merge';

	let {
		columns,
		rows,
		error,
		defaultSortKey,
		defaultSortOrder,
		title,
		hideHeader,
		class: styleClass,
		tableClass,
		onSelect,
		headers,
		data,
		children
	}: {
		columns: TableCol[];
		rows: ExtendedRow<T>[] | undefined | null;
		pending?: boolean;
		error?: any;
		placeholderRows?: number;
		defaultSortKey?: string;
		defaultSortOrder?: 'asc' | 'desc';
		title?: string | null;
		hideHeader?: boolean;
		class?: string;
		tableClass?: string;
		onSelect?: (row: ExtendedRow<T>) => void;
		headers?: Snippet<[TableCol]>;
		data?: Snippet<
			[ExtendedRow<T>, TableCol, (row: ExtendedRow<T>, rowKey: string | string[]) => any]
		>;
		children?: Snippet;
	} = $props();

	let sortKey = $derived(defaultSortKey ?? '');
	let sortOrder = $derived(defaultSortOrder ?? 'asc');
	let sortFunction = $state<((o: any) => any) | null>(null);

	const computedRows = $derived.by(() => {
		if (sortKey) {
			if (sortFunction) {
				return _.orderBy(rows, sortFunction, sortOrder);
			}
			return _.orderBy(rows, sortKey, sortOrder);
		} else {
			return rows;
		}
	});

	const getRowData = (
		row: ExtendedRow<T>,
		rowKey: string | string[],
		defaultValue: any = 'Failed to get cell value'
	) => {
		return _.get(row, rowKey, defaultValue);
	};

	const onSort = (key: string) => {
		sortKey = key;
		sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
		sortFunction = _.find(columns, { key })?.sortFunction ?? null;
	};
</script>

<div class={twMerge('w-full min-w-0', styleClass)}>
	{#if title}
		<h2 class="mb-3">{title}</h2>
	{/if}
	<div class={twMerge('bg-base-100 not-prose w-full', tableClass)}>
		<!-- Keyboard users need to scroll wide tables. -->
		<!-- svelte-ignore a11y_no_noninteractive_tabindex -->
		<div class="w-full overflow-x-auto" role="region" aria-label={title ?? 'Tabelle'} tabindex="0">
			<table class="m-0 w-full border-0 text-sm sm:text-base">
				{#if !hideHeader}
					<thead>
						<tr class="h-10">
							<td class={twMerge('border-b-accent/50 w-1 border-0 border-b-2')}> </td>

							{#each columns as column (column.key)}
								<th
									scope="col"
									aria-sort={column.sortable
										? sortKey === column.key
											? sortOrder === 'asc'
												? 'ascending'
												: 'descending'
											: 'none'
										: undefined}
									class="border-b-accent/50 border-0 border-b-2 p-2 text-center"
								>
									{#if column.sortable}
										<div
											class={twMerge(
												'flex items-center justify-center gap-1',
												column.class,
												column.tooltip ? 'tooltip tooltip-left' : '',
												column.tooltipClass
											)}
											data-tip={column.tooltip}
										>
											{#if headers}
												{@render headers(column)}
											{:else}
												{column.label}
											{/if}
											<button
												type="button"
												class="hover:bg-base-200 inline-flex h-11 w-8 shrink-0 items-center justify-center rounded"
												aria-label={`${column.tooltip ?? column.label ?? column.key} sortieren`}
												onclick={() => onSort(column.key)}
											>
												{#if sortKey !== column.key}
													<FontAwesomeIcon
														class="text-base-content hover:text-accent"
														icon={faSort}
													/>
												{:else if sortOrder === 'asc'}
													<FontAwesomeIcon
														class="text-base-content hover:text-accent"
														icon={faSortUp}
													/>
												{:else if sortOrder === 'desc'}
													<FontAwesomeIcon
														class="text-base-content hover:text-accent"
														icon={faSortDown}
													/>
												{/if}
											</button>
										</div>
									{:else}
										<div
											class={twMerge(
												column.class,
												column.tooltip ? 'tooltip tooltip-left' : '',
												column.tooltipClass
											)}
											data-tip={column.tooltip}
										>
											{#if headers}
												{@render headers(column)}
											{:else}
												{column.label}
											{/if}
										</div>
									{/if}
								</th>
							{/each}
						</tr>
					</thead>
				{/if}
				<tbody>
					{#if computedRows && computedRows.length !== 0}
						{#each computedRows as row (row.id)}
							<tr
								class={twMerge(
									onSelect ? 'hover:bg-base-200 focus-visible:bg-base-200 cursor-pointer' : '',
									row.highlight ? 'hover:bg-accent/50' : '',
									'border-base-content/10 h-12 border-b'
								)}
								onclick={() => onSelect?.(row)}
								onkeydown={(e) => {
									if (onSelect && e.key === 'Enter') {
										onSelect(row);
									}
								}}
								tabindex={onSelect ? 0 : -1}
							>
								<td
									class={twMerge(
										row.highlight ? 'bg-accent' : '',
										'w-1 border-0 text-nowrap whitespace-nowrap'
									)}
								>
									&nbsp;
								</td>
								{#each columns as column (column.key)}
									<td class="border-0 p-2 text-center">
										{#if data}
											{@render data(row, column, getRowData)}
										{:else}
											{getRowData(row, column.key)}
										{/if}
									</td>
								{/each}
							</tr>
						{/each}
					{:else if computedRows}
						<tr>
							<td colspan={columns.length + 1} class="text-accent h-10 border-none p-2 text-center">
								<FontAwesomeIcon icon={['fas', 'warning']} /> Keine Daten vorhanden
							</td>
						</tr>
					{:else}
						<tr>
							<td
								colspan={columns.length + 1}
								class="h-10 border-none p-2 text-center text-red-500"
							>
								<FontAwesomeIcon icon={['fas', 'circle-exclamation']} /> Fehler beim Laden der Daten
								{error}
							</td>
						</tr>
					{/if}
				</tbody>
				{@render children?.()}
			</table>
		</div>
	</div>
</div>
